import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type { paths } from '@snaphire/api-client';
import { administrativeMoneyDisplay, administrativeToday } from '@snaphire/contracts';
import { command, getObject, type GetPath, type Schema } from '../api/client';
import { sessionStore } from '../api/session';
import { Button, Empty, ErrorState, Field, Icon, Loading, Overlay, Select, useToast } from '../ui';
import './administrative.css';

export type Resource = Schema<'AdminResource'>;
export type Me = Schema<'AdminMe'>;
export const administrativeIntegrationsReady = true;
export const kindLabels = {
  SERVER: 'Серверы',
  DOMAIN: 'Домены',
  SERVICE: 'Сервисы',
  PROXY: 'Прокси',
};
export const adminNavigation = [
  { path: '', title: 'Обзор', icon: 'Utilities' },
  { path: 'sections', title: 'Разделы', icon: 'Bank' },
  { path: 'resources', title: 'Все ресурсы', icon: 'DocumentsPair' },
  { path: 'providers', title: 'Провайдеры', icon: 'Bank', permission: 'provider.read' },
  { path: 'projects', title: 'Проекты', icon: 'Bank', permission: 'settings.read' },
  {
    path: 'dictionaries',
    title: 'Справочники',
    icon: 'DocumentsPair',
    permission: 'settings.read',
  },
  { path: 'calendar', title: 'Календарь платежей', icon: 'Calendar' },
  { path: 'payments', title: 'История оплат', icon: 'DocumentsPair', permission: 'finance.read' },
  { path: 'members', title: 'Участники и доступы', icon: 'People', permission: 'members.read' },
  { path: 'roles', title: 'Роли и права', icon: 'Controls', permission: 'roles.edit' },
  { path: 'archive', title: 'Архив', icon: 'DocumentsPair' },
  { path: 'trash', title: 'Корзина', icon: 'Trash', permission: 'trash.read' },
  { path: 'audit', title: 'Журнал действий', icon: 'DocumentsPair', permission: 'audit.read' },
  { path: 'import', title: 'Импорт и экспорт', icon: 'ChevronDown' },
  { path: 'settings', title: 'Настройки базы', icon: 'Controls', permission: 'settings.read' },
  { path: 'telegram', title: 'Уведомления Telegram', icon: 'Bell' },
] as const;
const AdministrativeContext = createContext<Me | null>(null);
export function useAdministrative() {
  const value = useContext(AdministrativeContext);
  if (!value) throw new Error('Административное пространство не загружено');
  return value;
}
export function useAdministrativeMe() {
  const { account } = useSyncExternalStore(sessionStore.subscribe, sessionStore.getSnapshot);
  return useQuery({
    queryKey: ['administrative', account?.id, 'me'],
    queryFn: ({ signal }) => getObject<Me>('/api/v1/administrative/me', {}, signal),
    enabled: Boolean(account),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
export function AdministrativeFrame({ children }: { children: ReactNode }) {
  const query = useAdministrativeMe();
  const client = useQueryClient();
  const previous = useRef(query.data?.authorizationVersion);
  useEffect(() => {
    const next = query.data?.authorizationVersion;
    if (previous.current && next && previous.current !== next) {
      void client.cancelQueries({
        predicate: (q) => q.queryKey[0] === 'administrative' && q.queryKey[2] !== 'me',
      });
      client.removeQueries({
        predicate: (q) => q.queryKey[0] === 'administrative' && q.queryKey[2] !== 'me',
      });
    }
    previous.current = next;
  }, [query.data?.authorizationVersion, client]);
  if (query.error) return <ErrorState error={query.error} retry={() => void query.refetch()} />;
  if (!query.data) return <Loading />;
  return (
    <AdministrativeContext.Provider value={query.data}>
      <div className="admin-page" key={query.data.authorizationVersion}>
        {children}
      </div>
    </AdministrativeContext.Provider>
  );
}
export function AdministrativeNavigation({ path }: { path: string }) {
  const query = useAdministrativeMe();
  const navigationPath = path
    .replace(/^\/administrative\/sheets(?=\/|$)/, '/administrative/resources')
    .replace(/^\/administrative\/invites(?=\/|$)/, '/administrative/members');
  return (
    <>
      {adminNavigation
        .filter(
          (item) =>
            (administrativeIntegrationsReady ||
              [
                '',
                'sections',
                'resources',
                'providers',
                'calendar',
                'payments',
                'archive',
                'trash',
              ].includes(item.path)) &&
            (!('permission' in item) || query.data?.allowedActions.includes(item.permission)),
        )
        .map((item) => (
          <AdminLink
            key={item.path}
            path={item.path}
            className={`nav-item ${(item.path ? navigationPath.startsWith(`/administrative/${item.path}`) : navigationPath === '/administrative') ? 'active' : ''}`}
          >
            <Icon name={item.icon} />
            <span>{item.title}</span>
          </AdminLink>
        ))}
    </>
  );
}
export function administrativeDestination(path: string) {
  const [pathname, query] = path.split('?');
  const [page, entityId, operation] = (pathname || '').split('/');
  const search = Object.fromEntries(new URLSearchParams(query));
  if (page && entityId && operation)
    return {
      to: '/administrative/$page/$entityId/$operation' as const,
      params: { page, entityId, operation },
      search,
    };
  if (page && entityId)
    return { to: '/administrative/$page/$entityId' as const, params: { page, entityId }, search };
  if (page) return { to: '/administrative/$page' as const, params: { page }, search };
  return { to: '/administrative' as const };
}
export function AdminLink({
  path = '',
  children,
  className = '',
}: {
  path?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      {...administrativeDestination(path)}
      className={className}
      activeOptions={{ exact: true }}
    >
      {children}
    </Link>
  );
}
export function useAdminNavigate() {
  const navigate = useNavigate();
  return (path: string) => navigate(administrativeDestination(path));
}
export function useAdminQuery<T>(
  path: GetPath,
  params: Record<string, string> = {},
  filters: Record<string, unknown> = {},
  enabled = true,
) {
  const me = useAdministrative();
  return useQuery({
    queryKey: ['administrative', me.accountId, me.authorizationVersion, path, params, filters],
    queryFn: ({ signal }) => getObject<T>(path, params, signal, filters),
    enabled,
    refetchOnWindowFocus: true,
  });
}
export function useAdminList<T>(path: GetPath, filters: Record<string, unknown> = {}) {
  const me = useAdministrative();
  const query = useInfiniteQuery({
    queryKey: ['administrative', me.accountId, me.authorizationVersion, path, filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) =>
      getObject<{ items: T[]; nextCursor: string | null }>(path, {}, signal, {
        ...filters,
        limit: 100,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (last) => last.nextCursor || undefined,
    refetchOnWindowFocus: true,
  });
  return { ...query, items: query.data?.pages.flatMap((page) => page.items) || [] };
}
export function useAdminAction() {
  const client = useQueryClient();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const run = async <T,>(
    method: 'POST' | 'PATCH' | 'DELETE',
    path: keyof paths,
    body: unknown,
    params: Record<string, string> = {},
    message = 'Изменения сохранены',
  ): Promise<T | undefined> => {
    setPending(true);
    setError(null);
    try {
      const result = await command(method, path, body, params);
      await client.invalidateQueries({ queryKey: ['administrative'] });
      if (message) toast(message);
      return result as unknown as T;
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error('Не удалось выполнить операцию'));
      return undefined;
    } finally {
      setPending(false);
    }
  };
  return { run, pending, error, setError };
}
export function AdminForm({
  title,
  children,
  action,
  submit,
  cancel,
  label = 'Сохранить',
  headingLevel = 'h1',
}: {
  title: string;
  children: ReactNode;
  action: ReturnType<typeof useAdminAction>;
  submit: (data: FormData) => Promise<void>;
  cancel: string;
  label?: string;
  headingLevel?: 'h1' | 'h2';
}) {
  const navigate = useAdminNavigate();
  const Heading = headingLevel;
  return (
    <section className={`form-page ${headingLevel === 'h2' ? 'panel admin-nested-form' : ''}`}>
      <header className="admin-form-heading">
        <Heading>{title}</Heading>
      </header>
      <form
        className="domain-form"
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          void submit(data).catch((error: unknown) =>
            action.setError(
              error instanceof Error ? error : new Error('Проверьте заполненные поля'),
            ),
          );
        }}
      >
        {children}
        {action.error && <ErrorState error={action.error} />}
        <footer>
          <Button disabled={action.pending} onClick={() => void navigate(cancel)}>
            Отмена
          </Button>
          <Button type="submit" className="primary" disabled={action.pending}>
            {action.pending ? 'Сохранение…' : label}
          </Button>
        </footer>
      </form>
    </section>
  );
}
export function AdminSelect({
  label,
  name,
  options,
  value,
  onChange,
  required = false,
  emptyLabel,
}: {
  label: string;
  name?: string;
  options: { id: string; name: string }[];
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  emptyLabel?: string;
}) {
  return (
    <Field label={label}>
      <Select
        aria-label={label}
        name={name}
        value={onChange ? value : undefined}
        defaultValue={onChange ? undefined : value}
        onChange={(event) => onChange?.(event.target.value)}
        required={required}
      >
        <option value="">{emptyLabel || (required ? 'Выберите значение' : 'Не указано')}</option>
        {options.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}
export function AdminConfirm({
  title,
  children,
  action,
  onConfirm,
  onClose,
  label = 'Подтвердить',
}: {
  title: string;
  children: ReactNode;
  action: ReturnType<typeof useAdminAction>;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  label?: string;
}) {
  return (
    <Overlay title={title} variant="dialog" onClose={onClose}>
      <div className="admin-confirm">
        {children}
        {action.error && <ErrorState error={action.error} />}
      </div>
      <footer>
        <Button disabled={action.pending} onClick={onClose}>
          Отмена
        </Button>
        <Button className="primary" disabled={action.pending} onClick={() => void onConfirm()}>
          {action.pending ? 'Выполнение…' : label}
        </Button>
      </footer>
    </Overlay>
  );
}
export function QueryState({
  query,
}: {
  query: { isLoading: boolean; error: Error | null; refetch: () => unknown };
}) {
  return query.error ? (
    <ErrorState error={query.error} retry={() => void query.refetch()} />
  ) : query.isLoading ? (
    <Loading />
  ) : null;
}
export function Missing() {
  return <Empty title="Запись недоступна" subtitle="Проверьте адрес и права доступа." />;
}
export const text = (data: FormData, key: string) => String(data.get(key) ?? '').trim();
export const nullable = (data: FormData, key: string) => text(data, key) || null;
export function dateLabel(value?: string | null) {
  return value ? value.slice(0, 10).split('-').reverse().join('.') : 'Не указано';
}
export function dateTimeLabel(value: string, timezone: string) {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(value));
}
export function moneyLabel(value?: string | null, currency?: string | null) {
  return value == null
    ? 'Неизвестна'
    : `${administrativeMoneyDisplay(value, true)} ${currency || ''}`;
}
export const today = (me: Me) => administrativeToday(me.timezone);
export function InfoRows({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="admin-info">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value ?? 'Не указано'}</dd>
        </div>
      ))}
    </dl>
  );
}
